import { SeatInfo } from '../models/serializable/seating-state/seat-info.js';
import { SeatOrderType } from '../models/serializable/seating-state/seat-order-type.js';
import { SeatState } from '../models/serializable/seating-state/seat-state.js';
import { DatabaseIdGenerator } from '../utility/database-id-generator.js';
import { isNotNullOrUndefined } from '../utility/null-safety.js';
import { SerializationException } from '../exceptions/serialization-exception.js';
import { BinaryReader } from './binary-reader.js';
import { BinaryWriter } from './binary-writer.js';
import { fromEnumAsInt, toEnumFromInt } from './enum-utils.js';

export class SeatInfoSerializer {
  static serialize(entries: SeatInfo[]): Uint8Array {
    return this.asBytes(entries);
  }

  static deserialize(bytes: Uint8Array): SeatInfo[] {
    const reader = BinaryReader.fromBytes(bytes);
    const format = reader.readByte();

    switch(format) {
      case 1:
        return this.fromBytes(reader);
    }

    throw new SerializationException(`Seating state cannot be deserialized because format ${format} is unknown`);
  }

  private static asBytes(entries: SeatInfo[]): Uint8Array {
    const writer = new BinaryWriter();
    writer.writeByte(1);
    writer.writeInt32(entries.length);

    for(const entry of entries) {
      this.writeSeatInfo(writer, entry);
    }

    return writer.toMinimumBytes();
  }

  private static writeSeatInfo(writer: BinaryWriter, entry: SeatInfo): void {
    writer.writeByte(fromEnumAsInt(entry.state));
    writer.writeByte(fromEnumAsInt(entry.orderType));

    this.writeOrderId(writer, entry.orderId);
  }

  private static writeOrderId(writer: BinaryWriter, orderId: string | null): void {
    let i = 0;

    if(isNotNullOrUndefined(orderId)) {
      for(; i < orderId.length; i++) {
        writer.writeByte(orderId.charCodeAt(i));
      }
    }

    for(; i < DatabaseIdGenerator.kAutoIdLength; i++) {
      writer.writeByte(0);
    }
  }

  private static fromBytes(reader: BinaryReader): SeatInfo[] {
    const entries: SeatInfo[] = [];
    const numberOfEntries = reader.readInt32();

    for(let i = 0; i < numberOfEntries; i++) {
      entries.push(this.readSeatInfo(reader, i));
    }

    return entries;
  }

  private static readSeatInfo(reader: BinaryReader, index: number): SeatInfo {
    const state = toEnumFromInt(reader.readByte(), SeatState) as SeatState;
    const orderType = toEnumFromInt(reader.readByte(), SeatOrderType) as SeatOrderType;
    const orderId = this.readOrderId(reader);

    return new SeatInfo(
        index,
        state,
        orderType,
        orderId,
    );
  }

  private static readOrderId(reader: BinaryReader): string | null {
    let rawOrderId = '';
    let isValid = true;

    for(let i = 0; i < DatabaseIdGenerator.kAutoIdLength; i++) {
      const byte = reader.readByte();

      if(byte === 0) {
        isValid = false;
      }

      rawOrderId += String.fromCharCode(byte);
    }

    const orderId = isValid ? rawOrderId : null;

    return orderId;
  }
}
